import './Modelos.css';
import { useRef, useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../authContext';
import { useOpenRouter } from '../../context/OpenRouterContext';

const TIER_DEFAULTS = {
    fast: 'gemini-3.5-flash-lite',
    //thinking: 'gpt-5.5',
    pro: 'gpt-6.1-sol',
};

const FALLBACK_TIERS = {
    fast: ['gemini-3.5-flash-lite', 'gpt-5.6-terra', 'gpt-6-luna', 'claude-haiku-5.5', 'mistral-medium-3-5'],
    //thinking: ['gemini-2.5-pro'],
    pro: ['gpt-6.1-sol', 'gemini-3.8-flash', 'claude-opus-5.5', 'claude-sonnet-5.5', 'mistral-large-4-0', 'deepseek-v4.1-flash'],
};

const GEMINI_MODELS = new Set([
    'gemini-3.8-flash',
    'gemini-3.5-flash-lite',
    'google/gemini-3.8-flash',
    'google/gemini-3.5-flash-lite',
]);

const TIER_LABELS = {
    fast: 'Fast',
    //thinking: 'Thinking',
    pro: 'Pro',
    local: 'Local'
};

// utils
const getTierOfModel = (model, tiers) => {
    return Object.entries(tiers).find(([, models]) => models.includes(model))?.[0] ?? null;
};

function Modelos({ setModel, defaultModel, width = 'fit-content', email, disableLocalModels = false }) {
    const { accessToken } = useContext(AuthContext);
    const { t: tHub } = useTranslation('hub');
    const { useOpenrouter } = useOpenRouter();


    const modelLabels = {
        'gemini-3.8-flash': tHub('model_gemini_3_flash_preview'),
        'gemini-3.5-flash-lite': tHub('model_gemini_3_1_pro_preview'),
        'deepseek-r1:8b': 'Deepseek R1 8B',
        'claude-haiku-5.5': 'Claude Haiku 5.5',

        'claude-opus-5.5': 'Claude Opus 5.5',
        'claude-sonnet-5.5': 'Claude Sonnet 5.5',
        'mistral-medium-3-5': 'Mistral Medium 3.5',
        'mistral-large-4-0': 'Mistral Large 4',
        'gpt-6.1-sol': tHub('model_gpt_6_sol'),
        'gpt-5.6-terra': tHub('model_gpt_5_6_terra'),
        'gpt-6-luna': tHub('model_gpt_6_luna'),
        'deepseek-v4.1-flash': 'DeepSeek V4.1 Flash',
    };

    const [open, setOpen] = useState(false);
    const [subOpenTier, setSubOpenTier] = useState(null);
    const [tiers, setTiers] = useState(FALLBACK_TIERS);
    const [fetchedModels, setFetchedModels] = useState(false);
    const [publicClusterModels, setPublicClusterModels] = useState([]);

    const wrapperRef = useRef();
    const tierRefs = useRef({});

    const displayLabel = useMemo(() => {
        if (!defaultModel) return TIER_LABELS['pro'];

        const tier = getTierOfModel(defaultModel, tiers);

        if (tier) {
            const tierDefault = TIER_DEFAULTS[tier] ?? tiers[tier]?.[0];
            if (tierDefault === defaultModel) return TIER_LABELS[tier] || tier;
        }

        return modelLabels[defaultModel] || defaultModel;
    }, [defaultModel, tiers]);

    const selectedTier = useMemo(() => {
        return getTierOfModel(defaultModel, tiers) || 'pro';
    }, [defaultModel, tiers, modelLabels]);

    // fetch models immediately, check OpenRouter credits in background
    useEffect(() => {
        const get_llms = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/get_models`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({ email }),
                });

                if (!response.ok) throw new Error(`Erro: ${response.status}`);

                const data = await response.json();
                if (data.llm) {
                    const mapped = {};

                    data.llm.forEach(item => {
                        mapped[item.name] = item.models;
                    });

                    setTiers(mapped);
                } else {
                    setTiers(FALLBACK_TIERS);
                }
            } catch (err) {
                console.error('Erro ao buscar LLMs:', err);
                setTiers(FALLBACK_TIERS);
            } finally {
                setFetchedModels(true);
            }
        };

        if (email && !fetchedModels) get_llms();
    }, [email, accessToken, fetchedModels]);

    useEffect(() => {
        const fetchPublicClusterModels = async () => {
            try {
                const resp = await fetch(`${process.env.REACT_APP_API_URL}/get_public_cluster_models`, {
                    headers: { Authorization: `Bearer ${accessToken}` }
                });
                if (!resp.ok) return;
                const data = await resp.json();
                if (data.status && data.models?.length > 0) {
                    setPublicClusterModels(data.models);
                    setTiers(prev => ({ ...prev, local: data.models.map(m => m.id) }));
                }
            } catch {
                // silently ignore — cluster models are optional
            }
        };
        if (accessToken) fetchPublicClusterModels();
    }, [accessToken]);

    // click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
                setSubOpenTier(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handlePillClick = (e) => {
        e.stopPropagation();
        setOpen(prev => !prev);
        if (open) setSubOpenTier(null);
    };

    const handleSelectTier = (e, tier) => {
        e.stopPropagation();
        const model = TIER_DEFAULTS[tier] ?? tiers[tier]?.[0];
        if (model) setModel(model);
        setOpen(false);
        setSubOpenTier(null);
    };

    const handleToggleSub = (e, tier) => {
        e.stopPropagation();
        setSubOpenTier(prev => (prev === tier ? null : tier));
    };

    const handleSelectModel = (e, model) => {
        e.stopPropagation();
        setModel(model);
        setOpen(false);
        setSubOpenTier(null);
    };

    const getSubMenuTop = (tier) => {
        const el = tierRefs.current[tier];
        return el ? el.offsetTop : 0;
    };

    // Filter tiers and models based on OpenRouter credit availability
    const filteredTiers = useMemo(() => {
        if (useOpenrouter) {
            return tiers; // Show all tiers if credits available
        }
        // If credits < min, filter to show only Gemini models + keep Local always available
        const filtered = {};
        Object.entries(tiers).forEach(([tier, models]) => {
            if (tier === 'local') {
                // Always keep local models available
                filtered[tier] = models;
            } else {
                // Filter other tiers to show only Gemini models
                const geminiModels = models.filter(m => GEMINI_MODELS.has(m));
                if (geminiModels.length > 0) {
                    filtered[tier] = geminiModels;
                }
            }
        });
        return Object.keys(filtered).length > 0 ? filtered : { local: tiers.local || [] };
    }, [tiers, useOpenrouter]);

    const tierEntries = Object.entries(filteredTiers).filter(([tier]) => !(disableLocalModels && tier === 'local'));

    return (
        <section className="modelos-wrapper" style={{ width }} ref={wrapperRef}><div data-testid="model-selector" className={`right-pill ${open ? 'open' : ''}`} onClick={handlePillClick}>
                <span className="selected-model-label">{displayLabel}</span>
                <span className="chevron">{open ? '▲' : '▼'}</span>

                {open && (
                    <div className="tiers-popup" onClick={(e) => e.stopPropagation()}>
                        {tierEntries.map(([tier], index) => (
                            <div
                                data-testid={`tier-${tier}`}
                                key={tier}
                                ref={el => tierRefs.current[tier] = el}
                                className={`tier-item ${selectedTier === tier ? 'active' : ''} ${index === tierEntries.length - 1 ? 'last' : ''}`}
                            >
                                <span
                                    className="tier-label"
                                    onClick={(e) => handleSelectTier(e, tier)}
                                >
                                    {TIER_LABELS[tier] || tier}
                                </span>

                                <span
                                    data-testid={`tier-expand-${tier}`}
                                    className={`tier-expand ${subOpenTier === tier ? 'sub-open' : ''}`}
                                    onClick={(e) => handleToggleSub(e, tier)}
                                    onMouseEnter={(e) => {
                                        e.stopPropagation();
                                        setSubOpenTier(tier);
                                    }}
                                >
                                    ▶
                                </span>
                            </div>
                        ))}

                        {subOpenTier && filteredTiers[subOpenTier] && (
                            <div
                                className="models-popup"
                                style={{ top: getSubMenuTop(subOpenTier) }}
                                onMouseLeave={() => setSubOpenTier(null)}
                            >
                                {filteredTiers[subOpenTier].map((model) => {
                                    const clusterModel = publicClusterModels.find(m => m.id === model);
                                    const label = clusterModel
                                        ? `${typeof clusterModel.vision === 'string' ? clusterModel.vision + ' ' : ''}${model}`
                                        : (modelLabels[model] || model);
                                    return (
                                        <div
                                            data-testid={`model-${model}`}
                                            key={model}
                                            className={`model-item ${defaultModel === model ? 'selected' : ''}`}
                                            onClick={(e) => handleSelectModel(e, model)}
                                        >
                                            {label}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

export default Modelos;